<script setup lang="ts">
    import { reactive, onMounted, inject } from 'vue';
    import RangeSliderNative from '@shared/components/RangeSliderNative.vue';
    import { getServiceLevelPrices, type ServiceLevelPrice } from '@cwd/Account/Account.access';
    import Loading from '@shared/components/Loading.vue';
    import { loadCurrentSubscription } from '@shared/Root/Context.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { formatNumber } from '@shared/utils/formatNumber';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);

    const state = reactive<{
        selectedLimit: number;
        slider: number;
        showDetails: boolean;
        tiers: Tiers[];
        isLoading: boolean;
        currencyCode: string;
    }>({
        selectedLimit: 1000,
        slider: 1,
        showDetails: false,
        tiers: [],
        isLoading: true,
        currencyCode: '',
    });

    interface Tiers {
        name: string;
        levels: {
            limit: number;
            prices: {
                [key: string]: number;
            };
        }[];
    }

    onMounted(async () => {
        const serviceLevelPrices = await getServiceLevelPrices();
        state.tiers = [
            getTiers('Basic', serviceLevelPrices),
            getTiers('Pro', serviceLevelPrices),
            getTiers('Automation', serviceLevelPrices),
            getTiers('Enterprise', serviceLevelPrices),
        ];

        if (contextAccess.state.currentSubscription) {
            state.currencyCode = contextAccess.state.currentSubscription.ServiceLevelPrice.Currency.CurrencyCode;
        } else {
            const subscription = await loadCurrentSubscription();
            state.currencyCode = subscription?.ServiceLevelPrice?.Currency?.CurrencyCode ?? '';
        }

        state.isLoading = false;
    });

    const getTiers = (name: 'Basic' | 'Pro' | 'Enterprise' | 'Automation', data: ServiceLevelPrice[]): Tiers => {
        return {
            name: name,
            levels: data
                .filter((item) => item.ServiceSubscriptionTier.Name === name)
                .sort((a, b) => b.ServiceSubscriptionTier.Version - a.ServiceSubscriptionTier.Version)[0]
                .ServiceLevels.map((item) => {
                    let getPrices: { [key: string]: number } = {};
                    item.Currencies.forEach((itm) => {
                        getPrices[itm.Currency.CurrencyCode] = itm.MonthlyPrice;
                    });
                    return {
                        limit: item.ServiceLevel.ContactLimit,
                        prices: getPrices,
                    };
                }),
        };
    };

    const vEqHeight = {
        mounted: (el: HTMLElement, binding: { value: string }) => {
            eqHeight(binding.value);
            window.addEventListener('resize', () => eqHeight(binding.value));
        },
    };

    const contactLimits = [1000, 1500, 2000, 2500, 3000, 5000, 10000, 15000, 20000];

    const currentTierLevel = (levels: any[]) => {
        return levels.find((level) => level.limit >= state.selectedLimit);
    };
</script>

<script lang="ts">
    const eqHeight = (selector: string) => {
        const elements: NodeListOf<HTMLElement> = document.querySelectorAll(selector);
        let tallest: number = 0;
        elements.forEach((element: HTMLElement) => {
            element.style.minHeight = 'unset';
            if (tallest < element.getBoundingClientRect().height) tallest = element.getBoundingClientRect().height;
        });
        elements.forEach((element: HTMLElement) => (element.style.minHeight = tallest + 'px'));
    };
</script>

<template>
    <Loading v-if="state.isLoading" :show="true" :fixed="true" />

    <div class="container-fluid m-lg-t" v-else>
        <div class="max-700 m-auto-r m-xl-b m-auto-l text-center">
            <h1>{{ $Resources.AccountEvaluationHeading }}</h1>
            <p>{{ $Resources.AccountEvaluationText }}</p>

            <RangeSliderNative
                class="m-sm-b range-slider-lg"
                :modelValue="state.slider"
                @update:modelValue="(newValue) => (state.selectedLimit = contactLimits[newValue - 1])"
                label="[Number of contacts]"
                :min="1"
                :max="contactLimits.length + 1"></RangeSliderNative>

            <p :class="!state.selectedLimit && 'tier-limit-reached'">
                <span v-show="state.selectedLimit">
                    {{ $Resources.AccountEvaluationNumberOfContact1 }}
                    <strong>{{ formatNumber(state.selectedLimit) }}</strong>
                    {{ $Resources.AccountEvaluationNumberOfContact2 }}
                </span>
                <span v-show="!state.selectedLimit">
                    {{ $Resources.AccountEvaluationContactSales1 }}
                    <strong>{{ formatNumber(contactLimits[contactLimits.length - 1]) }}</strong>
                    <span v-html="$Resources.AccountEvaluationContactSales2"></span>
                </span>
            </p>
        </div>

        <div class="tiers-container">
            <div class="tiers d-flex flex-wrap gap-sm flex-even justify-content-center m-auto-r m-auto-l">
                <div
                    class="tier p-relative"
                    :class="{ 'tier-recomended': tier.name === 'Pro', disabled: !currentTierLevel(tier.levels) }"
                    v-for="(tier, index) in state.tiers"
                    :key="tier.name">
                    <h2 class="h3">
                        {{ tier.name }}
                        <span v-if="tier.name === 'Pro'" class="badge-recommended p-absolute text-sm c-white bg-primary fw-600">
                            {{ $Resources.AccountEvaluationBadgeRecommended }}
                        </span>
                    </h2>

                    <p class="tier-description" v-eq-height="'.tier-description'">
                        {{ $Resources[tier.name + 'TierDescription'] }}
                    </p>

                    <p>
                        {{ $Resources.AccountEvaluationPerMonth }}:
                        <br />
                        <span class="h2 strong">
                            <span v-if="currentTierLevel(tier.levels)">{{ formatNumber(currentTierLevel(tier.levels).prices[state.currencyCode]) }}</span>
                            <span v-else>-</span>
                        </span>
                        {{ state.currencyCode }}
                    </p>

                    <p class="d-flex flex-wrap m-xs-b">
                        <span class="m-auto-r">{{ $Resources.AccountEvaluationMaxContacts }}:</span>
                        <span class="strong" v-if="currentTierLevel(tier.levels)">{{ formatNumber(currentTierLevel(tier.levels).limit) }}</span>
                        <span class="strong" v-else>-</span>
                    </p>

                    <p class="d-flex flex-wrap m-md-b">
                        <span class="m-auto-r">{{ $Resources.AccountEvaluationMaxMailings }}:</span>
                        <span class="strong" v-if="currentTierLevel(tier.levels)">{{ formatNumber(currentTierLevel(tier.levels).limit * 4) }}</span>
                        <span class="strong" v-else>-</span>
                    </p>

                    <div class="m-sm-b">
                        <a
                            v-if="currentTierLevel(tier.levels)"
                            :href="`/Account/Upgrade?contactsLimit=${currentTierLevel(tier.levels).limit - 1}&emailLimit=${
                                currentTierLevel(tier.levels).limit * 4 - 1
                            }&tier=${tier.name}&evaluation=true`"
                            class="btn btn-cta btn-block btn-lg">
                            {{ $Resources.AccountEvaluationCtaButton }}
                        </a>
                        <span v-else disabled class="btn btn-cta btn-block btn-lg">{{ $Resources.AccountEvaluationCtaButton }}</span>
                    </div>

                    <div class="m-sm-b tiers-hide" v-show="!state.showDetails">
                        <button class="btn btn-subtle btn-block" @click="state.showDetails = true">
                            {{ $Resources.AccountEvaluationShowDetailsButton }}
                        </button>
                    </div>

                    <h3 class="h4 m-md-t tiers-show" v-show="state.showDetails">{{ $Resources[tier.name + 'TierFeatureListHeading'] }}:</h3>

                    <ul class="features tiers-show" v-show="state.showDetails" v-html="$Resources[tier.name + 'TierFeatureList']"></ul>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="less">
    .tiers-container {
        container-type: inline-size;

        .tiers {
            max-width: 1400px;
        }

        @container (max-width: 1100px) {
            .tiers {
                max-width: 695px !important;

                .tier {
                    flex-basis: calc(50% - 0.313rem);
                }
            }
        }

        @container (max-width: 546px) {
            .tiers {
                max-width: 343px !important;

                .tier {
                    flex-basis: 100%;
                }
            }
        }

        @container (min-width: 1100px) {
            .tiers-hide {
                display: none !important;
            }

            .tiers-show {
                display: block !important;
            }
        }
    }

    .tiers {
        margin: 0 auto var(--ug-spacing-lg);

        .tier {
            border: 1px solid var(--border-color);
            border-radius: var(--border-radius);
            padding: 0 var(--ug-spacing-lg) var(--ug-spacing-lg);
        }

        .tier-recomended {
            border-color: var(--brand-primary-500);
            box-shadow: 0 0 0 1px var(--brand-primary-500);
        }
    }

    .features {
        list-style: none;
        padding-left: 30px;

        li {
            margin-bottom: var(--ug-spacing-xs);
            position: relative;
        }

        li:before {
            content: '';
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath d='M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z'/%3E%3C/svg%3E");
            position: absolute;
            top: 0px;
            left: -25px;
            width: 16px;
            height: 16px;
        }
    }

    .range-slider-lg {
        --slider-height: 10px;
        --slider-thumb-size: 24px;

        input[type='text'],
        label {
            display: none;
        }
    }

    .badge-recommended {
        top: 0;
        right: 30px;
        padding: var(--ug-spacing-xs);
        border-radius: 0 0 var(--border-radius) var(--border-radius);
        line-height: 1;
    }

    .tier-limit-reached a {
        font-weight: 600;
    }
</style>
